"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { signIn } from "next-auth/react";

export default function Register() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to register");
        setLoading(false);
      } else {
        // Automatically sign in after registration
        const signInRes = await signIn("credentials", {
          email,
          password,
          redirect: false,
        });
        
        if (signInRes?.error) {
          setError(signInRes.error);
          setLoading(false);
        } else {
          router.push("/subscribe");
          router.refresh();
        }
      }
    } catch (err) {
      setError("An unexpected error occurred");
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] flex">
      {/* Left Column - Branding / Art (Hidden on Mobile) */}
      <div className="hidden lg:flex w-1/2 relative bg-slate-900 border-r border-white/10 items-center justify-center overflow-hidden">
        {/* Background Gradients */}
        <div className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] rounded-full bg-blue-900/30 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[60%] h-[60%] rounded-full bg-emerald-900/20 blur-[120px]" />
        
        <div className="z-10 max-w-lg p-12">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 shadow-[0_0_30px_rgba(16,185,129,0.4)] flex items-center justify-center font-bold text-white text-xl mb-8">
            S
          </div>
          <h2 className="text-5xl font-bold text-white mb-6 leading-tight">
            Institutional logic.<br />
            Now accessible.
          </h2>
          <p className="text-lg text-slate-400 mb-8 leading-relaxed">
            Join the Stratos ecosystem and execute 6 concurrent mathematical models across the S&P500 grid natively.
          </p>
          <div className="flex items-center gap-4 text-sm text-slate-500 font-medium">
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> Live Execution</span>
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-500"></span> Institutional Grade</span>
          </div>
        </div>
      </div>

      {/* Right Column - Registration Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-24 relative">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">Create Account</h1>
            <p className="text-slate-400">Enter your details to join Stratos</p>
          </div>

          {error && (
            <div className="bg-rose-500/10 border border-rose-500/50 text-rose-400 p-4 rounded-xl text-sm mb-8">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Email Address</label>
              <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white px-5 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all placeholder:text-slate-600"
                placeholder="admin@stratos.com"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Password</label>
              <input 
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white px-5 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all placeholder:text-slate-600"
                placeholder="••••••••"
                required
                minLength={6}
              />
            </div>
            
            <button 
              type="submit" 
              disabled={loading}
              className="w-full py-4 rounded-xl font-bold text-white bg-emerald-600 hover:bg-emerald-500 focus:ring-4 focus:ring-emerald-500/50 transition-all disabled:opacity-50 mt-4"
            >
              {loading ? "Initializing..." : "Sign Up"}
            </button>
          </form>

          <div className="mt-10 text-center text-sm text-slate-500">
            Already have an account?{" "}
            <Link href="/login" className="text-white hover:text-emerald-400 font-medium transition-colors border-b border-white hover:border-emerald-400 pb-0.5">
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
